import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthSafe } from "./auth-context";
import { createDoltClient, DoltClientError } from "./dolt-client";
import type { DraftPayload, LoadResult } from "./dolt-client";
import { isDoltBackend } from "./dolt-mode";
import {
  clearDoltPersistence,
  configureDoltPersistence,
  getDoltLocalRecovery,
  markDoltSaved,
} from "./dolt-persistence";
import type { DoltLocalRecovery } from "./dolt-persistence";
import { supabase } from "./supabase-client";
import { useScenario } from "./state";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const WORKSPACES_URL = "/api/data/v1/workspaces";
const TAB_STORAGE_KEY = "tenure.dolt.tab.v1";

type DoltClient = ReturnType<typeof createDoltClient>;
type Grant = {
  orgId: string;
  role: string;
  moduleLevel: string;
  canWrite: boolean;
  canIssue: boolean;
};

export type DoltWorkspaceContextValue = {
  client: DoltClient;
  grant: Grant;
  revision: number;
  savedPayload: DraftPayload | null;
  afterSave: (revision: number, payload: DraftPayload) => void;
  reload: () => void;
};

const DoltWorkspaceContext = createContext<DoltWorkspaceContextValue | null>(
  null,
);

export function useDoltWorkspace(): DoltWorkspaceContextValue {
  const value = useContext(DoltWorkspaceContext);
  if (!value)
    throw new Error("useDoltWorkspace must be inside DoltWorkspaceGate");
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validGrants(value: unknown): Grant[] {
  if (!isRecord(value) || !Array.isArray(value.workspaces)) {
    throw new Error("Malformed workspaces response");
  }
  return value.workspaces.map((value): Grant => {
    if (
      !isRecord(value) ||
      typeof value.orgId !== "string" ||
      !UUID_RE.test(value.orgId) ||
      typeof value.role !== "string" ||
      value.role.trim() === "" ||
      typeof value.moduleLevel !== "string" ||
      value.moduleLevel.trim() === "" ||
      typeof value.canWrite !== "boolean" ||
      typeof value.canIssue !== "boolean"
    ) {
      throw new Error("Malformed workspace grant record");
    }
    return value as Grant;
  });
}

function decodeSub(token: string): string | null {
  try {
    const encoded = token.split(".")[1];
    if (!encoded) return null;
    const padded = encoded
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(encoded.length / 4) * 4, "=");
    const claims: unknown = JSON.parse(atob(padded));
    return isRecord(claims) && typeof claims.sub === "string"
      ? claims.sub
      : null;
  } catch {
    return null;
  }
}

function tabId(): string {
  try {
    const existing = sessionStorage.getItem(TAB_STORAGE_KEY);
    if (existing && UUID_RE.test(existing)) return existing;
    const next = crypto.randomUUID();
    sessionStorage.setItem(TAB_STORAGE_KEY, next);
    return next;
  } catch {
    return crypto.randomUUID();
  }
}

function canonicalJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalJson);
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, canonicalJson(value[key])]),
    );
  }
  return value;
}

function equalPayload(
  left: DraftPayload | null,
  right: DraftPayload | null,
): boolean {
  return (
    JSON.stringify(canonicalJson(left)) === JSON.stringify(canonicalJson(right))
  );
}

function describe(error: unknown): string {
  if (error instanceof DoltClientError)
    return error.currentRevision === undefined
      ? error.code
      : `${error.code} (saved revision ${error.currentRevision})`;
  return error instanceof Error ? error.message : "Unexpected workspace error";
}

function scopeKey(
  issuer: string,
  userId: string,
  workspaceId: string,
  currentTabId: string,
): string {
  return `tenure.dolt.local.v1:${encodeURIComponent(issuer)}:${encodeURIComponent(userId)}:${encodeURIComponent(workspaceId)}:${encodeURIComponent(currentTabId)}`;
}

function downloadJson(name: string, payload: DraftPayload): void {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

export function DoltWorkspaceGate({ children }: { children: React.ReactNode }) {
  return isDoltBackend() ? (
    <DoltWorkspaceRequired>{children}</DoltWorkspaceRequired>
  ) : (
    <>{children}</>
  );
}

export function DoltWorkspaceRequired({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useAuthSafe();
  const userId = auth?.user?.id;
  const [currentTabId, setCurrentTabId] = useState<string | null>(null);
  const [grants, setGrants] = useState<Grant[] | null>(null);
  const [grantsUserId, setGrantsUserId] = useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<DoltClient | null>(null);
  const [loaded, setLoaded] = useState<LoadResult | null>(null);
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const [local, setLocal] = useState<DoltLocalRecovery | null>(null);
  const [choice, setChoice] = useState<"none" | "local" | "latest-confirm">(
    "none",
  );
  const [configuredScope, setConfiguredScope] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  useEffect(() => setCurrentTabId(tabId()), []);

  const issuer = `${(import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/+$/, "")}/auth/v1`;
  const freshGrants = grantsUserId === userId ? grants : null;
  const grant = useMemo(
    () => freshGrants?.find((item) => item.orgId === workspaceId) ?? null,
    [freshGrants, workspaceId],
  );
  const identityScope =
    userId && workspaceId && currentTabId
      ? `${userId}:${workspaceId}:${currentTabId}`
      : null;
  const readyScope =
    identityScope === null ? null : `${identityScope}:${attempt}`;

  // Reset synchronously for the next render before effects can configure a new identity.
  useEffect(() => {
    setClient(null);
    setLoaded(null);
    setLoadedScope(null);
    setLocal(null);
    setChoice("none");
    setConfiguredScope(null);
    setPendingMessage(null);
    setError(null);
    clearDoltPersistence();
  }, [identityScope, attempt]);

  useEffect(() => {
    if (!userId || !UUID_RE.test(userId) || !currentTabId) {
      setGrants(null);
      setGrantsUserId(null);
      setWorkspaceId(null);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token || decodeSub(token) !== userId)
          throw new Error("Could not verify your session; sign in again.");
        const response = await fetch(WORKSPACES_URL, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body: unknown = await response.json();
        if (!response.ok)
          throw new Error(`Workspace request failed (${response.status})`);
        const next = validGrants(body);
        if (!active) return;
        setGrants(next);
        setGrantsUserId(userId);
        setWorkspaceId((previous) =>
          previous && next.some((item) => item.orgId === previous)
            ? previous
            : next.length === 1
              ? next[0].orgId
              : null,
        );
        setError(
          next.length === 0
            ? "No Dolt private draft workspace assigned."
            : null,
        );
      } catch (caught) {
        if (active) {
          setGrants(null);
          setGrantsUserId(null);
          setError(describe(caught));
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [userId, currentTabId, attempt]);

  useEffect(() => {
    if (!userId || !workspaceId || !currentTabId || !grant || !readyScope)
      return;
    let active = true;
    const loadScope = readyScope;
    const created = createDoltClient({
      fetchImpl: fetch,
      storage: localStorage,
      tabId: currentTabId,
      identity: { issuer, userId, workspaceId },
      getToken: async () => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token ?? null;
        return token && decodeSub(token) === userId ? token : null;
      },
    });
    void (async () => {
      try {
        let result = await created.load();
        if (result.pending) {
          const pendingState = await created.reconcilePending();
          if (pendingState.status === "acknowledged")
            result = await created.load();
        }
        if (!active) return;
        setClient(created);
        setLoaded(result);
        setLoadedScope(loadScope);
        setLocal(
          getDoltLocalRecovery(
            scopeKey(issuer, userId, workspaceId, currentTabId),
          ),
        );
        setError(null);
      } catch (caught) {
        if (active) setError(describe(caught));
      }
    })();
    return () => {
      active = false;
      created.dispose();
    };
  }, [userId, workspaceId, currentTabId, issuer, grant, readyScope]);

  const configure = useCallback(
    (payload: DraftPayload | null) => {
      if (
        !userId ||
        !workspaceId ||
        !currentTabId ||
        !loaded ||
        loadedScope !== readyScope ||
        !readyScope
      )
        return;
      configureDoltPersistence({
        payload,
        storageKey: scopeKey(issuer, userId, workspaceId, currentTabId),
        baseRevision: loaded.revision,
      });
      setConfiguredScope(readyScope);
    },
    [
      issuer,
      userId,
      workspaceId,
      currentTabId,
      loaded,
      loadedScope,
      readyScope,
    ],
  );

  useEffect(() => {
    if (
      !loaded ||
      loadedScope !== readyScope ||
      !readyScope ||
      configuredScope === readyScope ||
      loaded.pending
    )
      return;
    if (local && !equalPayload(local.payload, loaded.payload)) return;
    configure(loaded.payload);
  }, [loaded, loadedScope, local, configure, readyScope, configuredScope]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  const afterSave = useCallback((revision: number, payload: DraftPayload) => {
    markDoltSaved(revision);
    setLoaded((previous) =>
      previous ? { ...previous, revision, payload, pending: null } : previous,
    );
  }, []);

  if (
    client &&
    grant &&
    loaded &&
    loadedScope === readyScope &&
    configuredScope === readyScope
  ) {
    return (
      <DoltWorkspaceContext.Provider
        value={{
          client,
          grant,
          revision: loaded.revision,
          savedPayload: loaded.payload,
          afterSave,
          reload,
        }}
      >
        {children}
      </DoltWorkspaceContext.Provider>
    );
  }
  if (!auth?.user || !userId || !UUID_RE.test(userId)) {
    return (
      <Notice
        title="Sign in required"
        detail="A verified account is required before a private draft can open."
      />
    );
  }
  if (error)
    return (
      <Notice
        title="Private draft unavailable"
        detail={error}
        retry={reload}
        signOut={auth.signOut}
      />
    );
  if (freshGrants && freshGrants.length > 1 && !workspaceId) {
    return (
      <section className="card" aria-label="Choose private workspace">
        <h2>Choose private workspace</h2>
        {freshGrants.map((item) => (
          <button
            className="btn"
            key={item.orgId}
            onClick={() => setWorkspaceId(item.orgId)}
          >
            {item.orgId}
          </button>
        ))}
      </section>
    );
  }
  if (!client || !loaded)
    return (
      <Notice
        title="Opening private draft"
        detail="Loading the saved private draft before the workspace can open."
      />
    );
  if (loaded.pending) {
    const retryPending = async () => {
      try {
        await client.save(loaded.pending!.payload);
        reload();
      } catch (caught) {
        setPendingMessage(
          `Previous private save was not confirmed: ${describe(caught)}`,
        );
      }
    };
    const discardPending = async () => {
      downloadJson(
        "tenure-pending-private-draft.json",
        loaded.pending!.payload,
      );
      try {
        await client.discardPending();
        reload();
      } catch (caught) {
        setPendingMessage(
          `Could not discard the uncertain private save: ${describe(caught)}`,
        );
      }
    };
    return (
      <Recovery
        title="A previous save needs confirmation"
        detail="The prior save outcome remains uncertain. Retry its exact saved copy before opening this workspace."
        message={pendingMessage}
        actions={
          <>
            <button
              className="btn"
              onClick={() =>
                downloadJson(
                  "tenure-pending-private-draft.json",
                  loaded.pending!.payload,
                )
              }
            >
              Download pending JSON
            </button>
            <button className="btn" onClick={() => void retryPending()}>
              Retry previous private save
            </button>
            {choice === "latest-confirm" ? (
              <button className="btn" onClick={() => void discardPending()}>
                Confirm discard and load latest saved draft
              </button>
            ) : (
              <button
                className="btn"
                onClick={() => setChoice("latest-confirm")}
              >
                Discard uncertain save
              </button>
            )}
          </>
        }
      />
    );
  }
  if (local && !equalPayload(local.payload, loaded.payload)) {
    const resume = local.baseRevision === loaded.revision;
    return (
      <Recovery
        title="Local private edits found"
        detail={
          resume
            ? "These edits are not in the latest saved draft."
            : "These edits are based on an older saved revision. The local copy will be preserved."
        }
        actions={
          <>
            <button
              className="btn"
              onClick={() =>
                downloadJson("tenure-local-private-draft.json", local.payload)
              }
            >
              Download local JSON
            </button>
            {resume ? (
              <button className="btn" onClick={() => configure(local.payload)}>
                Resume local edits
              </button>
            ) : choice === "latest-confirm" ? (
              <button className="btn" onClick={() => configure(loaded.payload)}>
                Confirm use latest saved draft
              </button>
            ) : (
              <button
                className="btn"
                onClick={() => setChoice("latest-confirm")}
              >
                Use latest saved draft
              </button>
            )}
          </>
        }
      />
    );
  }
  return (
    <Notice
      title="Opening private draft"
      detail="Configuring the private workspace."
    />
  );
}

function Notice({
  title,
  detail,
  retry,
  signOut,
}: {
  title: string;
  detail: string;
  retry?: () => void;
  signOut?: () => Promise<void>;
}) {
  return (
    <section className="card" role="alert">
      <h2>{title}</h2>
      <p>{detail}</p>
      {retry && (
        <button className="btn" onClick={retry}>
          Retry
        </button>
      )}
      {signOut && (
        <button className="btn" onClick={() => void signOut()}>
          Sign out
        </button>
      )}
    </section>
  );
}

function Recovery({
  title,
  detail,
  actions,
  message,
}: {
  title: string;
  detail: string;
  actions: React.ReactNode;
  message?: string | null;
}) {
  return (
    <section className="card" role="alert">
      <h2>{title}</h2>
      <p>{detail}</p>
      {message && <p role="status">{message}</p>}
      <div>{actions}</div>
    </section>
  );
}

function payloadFromScenario(
  scenario: ReturnType<typeof useScenario>,
): DraftPayload {
  return {
    schemaVersion: 1,
    scenarios: scenario.scenarios,
    sites: scenario.sites,
    activeId: scenario.activeId,
  };
}

export function DoltDraftBar() {
  if (!isDoltBackend()) return null;
  return <DoltDraftBarRequired />;
}

function DoltDraftBarRequired() {
  const scenario = useScenario();
  const { client, grant, revision, savedPayload, afterSave } =
    useDoltWorkspace();
  const payload = payloadFromScenario(scenario);
  const dirty = !equalPayload(payload, savedPayload);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const save = useCallback(async () => {
    const snapshot = JSON.parse(JSON.stringify(payload)) as DraftPayload;
    setSaving(true);
    setMessage(null);
    try {
      const ack = await client.save(snapshot);
      if (!mounted.current) return;
      afterSave(ack.revision, snapshot);
      setMessage("Private draft saved.");
    } catch (caught) {
      if (mounted.current)
        setMessage(
          `Private save was not confirmed: ${describe(caught)}. Download your current JSON or review saved revisions.`,
        );
    } finally {
      if (mounted.current) setSaving(false);
    }
  }, [client, payload, afterSave]);
  return (
    <section
      className="card"
      aria-label="Private draft controls"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "center",
        padding: 16,
      }}
    >
      <strong>
        {dirty
          ? "Unsaved private edits"
          : `Private draft saved · revision ${revision}`}
      </strong>
      <button
        className="btn"
        disabled={!grant.canWrite || !dirty || saving}
        onClick={() => void save()}
      >
        {saving ? "Saving private draft…" : "Save private draft"}
      </button>
      <button
        className="btn"
        onClick={() =>
          downloadJson("tenure-current-private-draft.json", payload)
        }
      >
        Download current JSON
      </button>
      <a className="btn" href="#/versions">
        Review saved revisions
      </a>
      {message && <p role="status">{message}</p>}
    </section>
  );
}

const MISSING = Symbol("missing");
type Difference = {
  path: string;
  before: unknown | typeof MISSING;
  after: unknown | typeof MISSING;
};
function flatten(
  value: unknown,
  path = "",
  result = new Map<string, unknown>(),
): Map<string, unknown> {
  if (Array.isArray(value)) {
    if (value.length === 0) result.set(path, []);
    else
      value.forEach((item, index) =>
        flatten(item, path ? `${path}.${index}` : String(index), result),
      );
  } else if (isRecord(value)) {
    const keys = Object.keys(value);
    if (keys.length === 0) result.set(path, {});
    else
      keys.forEach((key) =>
        flatten(value[key], path ? `${path}.${key}` : key, result),
      );
  } else result.set(path, value);
  return result;
}

export function DoltHistory() {
  if (!isDoltBackend()) return null;
  return <DoltHistoryRequired />;
}

function displayDifference(value: unknown | typeof MISSING): string {
  return value === MISSING ? "— missing —" : JSON.stringify(value);
}

function DoltHistoryRequired() {
  const { client } = useDoltWorkspace();
  const scenario = useScenario();
  const [revisions, setRevisions] = useState<number[]>([]);
  const [differences, setDifferences] = useState<Difference[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);
  const liveClient = useRef(client);
  liveClient.current = client;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, [client]);
  const load = useCallback(async () => {
    try {
      const result = await client.history();
      if (!mounted.current || liveClient.current !== client) return;
      setRevisions(
        result
          .filter(isRecord)
          .map((entry) => entry.revision)
          .filter(
            (revision): revision is number => typeof revision === "number",
          ),
      );
      setError(null);
    } catch (caught) {
      if (mounted.current && liveClient.current === client)
        setError(describe(caught));
    }
  }, [client]);
  const compare = useCallback(
    async (revision: number) => {
      const working = payloadFromScenario(scenario);
      try {
        const prior = await client.getRevision(revision);
        if (!mounted.current || liveClient.current !== client) return;
        const before = flatten(prior.payload);
        const after = flatten(working);
        const paths = new Set([...before.keys(), ...after.keys()]);
        setDifferences(
          [...paths]
            .sort()
            .map((path) => ({
              path,
              before: before.has(path) ? before.get(path)! : MISSING,
              after: after.has(path) ? after.get(path)! : MISSING,
            }))
            .filter(
              (item) =>
                item.before === MISSING ||
                item.after === MISSING ||
                JSON.stringify(canonicalJson(item.before)) !==
                  JSON.stringify(canonicalJson(item.after)),
            ),
        );
      } catch (caught) {
        if (mounted.current && liveClient.current === client)
          setError(describe(caught));
      }
    },
    [client, scenario],
  );
  return (
    <section className="card" aria-label="Private draft history">
      <h2>Saved revisions</h2>
      <button className="btn" onClick={() => void load()}>
        Load saved revisions
      </button>
      {error && <p role="alert">{error}</p>}
      <ul>
        {revisions.map((revision) => (
          <li key={revision}>
            <button className="btn" onClick={() => void compare(revision)}>
              Revision {revision}
            </button>
          </li>
        ))}
      </ul>
      {differences &&
        (differences.length === 0 ? (
          <p>No input differences from this saved revision.</p>
        ) : (
          <>
            <p>
              {differences.length} changed fields (showing up to 200). This is
              an input comparison, not financial-impact or release approval.
            </p>
            <table>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Saved revision</th>
                  <th>Working draft</th>
                </tr>
              </thead>
              <tbody>
                {differences.slice(0, 200).map((item) => (
                  <tr key={item.path}>
                    <th>{item.path}</th>
                    <td>{displayDifference(item.before)}</td>
                    <td>{displayDifference(item.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ))}
    </section>
  );
}
